<template>
    <v-card class="rounded-lg px-3 py-3 pir-progression-card">
        <v-card-title class="secondary-font py-0 pir-progression-title">
            MVP Ladder (Top 10 Players by Average PIR)
        </v-card-title>

        <div class="pir-progression-title-line" aria-hidden="true" />

        <div v-if="rankedPlayers.length" class="pir-ranking-wrap" role="img" aria-label="Top 10 players by average PIR">
            <div
                v-for="(player, index) in rankedPlayers"
                :key="player.key || index"
                :class="['pir-ranking-row', podiumClass(index), { 'is-clickable': player.routeId }]"
                :role="player.routeId ? 'button' : null"
                :tabindex="player.routeId ? 0 : null"
                @click="player.routeId && openPlayerProfile(player.routeId)"
                @keyup.enter="player.routeId && openPlayerProfile(player.routeId)"
                @keyup.space="player.routeId && openPlayerProfile(player.routeId)"
            >
                <img
                    v-if="player.teamLogoSrc"
                    class="pir-ranking-watermark"
                    :src="player.teamLogoSrc"
                    :alt="player.teamLabel"
                >

                <div class="pir-ranking-rank">{{ index + 1 }}</div>

                <img
                    class="pir-ranking-avatar"
                    :src="player.imageSrc"
                    :alt="player.label"
                    @error="handleImageError"
                >

                <div class="pir-ranking-main">
                    <div class="pir-ranking-meta">
                        <span class="pir-ranking-name">{{ player.label }}</span>
                        <span class="pir-ranking-team">{{ player.teamLabel }}</span>
                    </div>

                    <div class="pir-ranking-track">
                        <div
                            class="pir-ranking-bar"
                            :style="{ width: `${player.barWidth}%`, backgroundColor: player.color }"
                        />
                    </div>
                </div>

                <div class="pir-ranking-value">{{ player.averageLabel }}</div>
            </div>
        </div>

        <div v-else class="pir-empty-state">
            No player data to display
        </div>
    </v-card>
</template>

<script>
    export default {
        name: 'PirProgressionChart',
        props: {
            players: {
                type: Array,
                default: () => []
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png'),
                defaultSeriesColors: [
                    '#ef7f33',
                    '#4f7cff',
                    '#2f9e44',
                    '#d97706',
                    '#0ea5e9',
                    '#ef4444',
                    '#14b8a6',
                    '#8b5cf6',
                    '#f59e0b',
                    '#22c55e'
                ]
            }
        },
        computed: {
            preparedPlayers() {
                return (Array.isArray(this.players) ? this.players : [])
                    .map((player, index) => {
                        const history = this.getPirHistory(player)
                        const matchesPlayed = history.length
                        const averagePir = Number.isFinite(Number(player.averagePir))
                            ? Number(player.averagePir)
                            : (matchesPlayed ? history.reduce((sum, value) => sum + value, 0) / matchesPlayed : 0)
                        const color = this.normalizeColor(player.teamColor) || this.defaultSeriesColors[index % this.defaultSeriesColors.length]

                        return {
                            key: this.getPlayerKey(player, index),
                            routeId: this.getPlayerRouteId(player),
                            label: this.getPlayerLegendLabel(player, index),
                            imageSrc: this.getAvatarSrc(player),
                            teamLogoSrc: this.getTeamLogoSrc(player),
                            teamLabel: player.team || player.teamName || 'Team',
                            averagePir,
                            matchesPlayed,
                            color
                        }
                    })
                    .filter(player => Number.isFinite(player.averagePir) && player.matchesPlayed > 0)
            },
            maxPir() {
                const highest = Math.max(0, ...this.preparedPlayers.map(player => Number(player.averagePir) || 0))
                return Math.max(1, highest)
            },
            rankedPlayers() {
                return this.preparedPlayers
                    .slice()
                    .sort((a, b) => b.averagePir - a.averagePir)
                    .slice(0, 10)
                    .map(player => ({
                        ...player,
                        averageLabel: Number(player.averagePir || 0).toFixed(1),
                        barWidth: this.maxPir ? Math.max(6, (Number(player.averagePir || 0) / this.maxPir) * 100) : 0
                    }))
            }
        },
        methods: {
            podiumClass(index) {
                const rank = Number(index) + 1
                if (rank > 3) return ''
                return `is-podium rank-${rank}`
            },
            getPlayerRouteId(player = {}) {
                return player.id || player.playerId || player._id || player.uuid || player.player?.id || player.player?.playerId || null
            },
            openPlayerProfile(playerId) {
                if (!playerId) {
                    return
                }

                this.$router.push({ name: 'Player', params: { id: playerId } })
            },
            handleImageError(event) {
                if (event && event.target) {
                    event.target.src = this.defaultPlayerImg
                }
            },
            normalizeColor(value) {
                if (!value || typeof value !== 'string') return null
                const trimmed = value.trim()
                const cssColorRegex = /^(#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})|rgb\(|rgba\(|hsl\(|hsla\()/
                return cssColorRegex.test(trimmed) ? trimmed : null
            },
            getAvatarSrc(player = {}) {
                const raw = player.imageSrc || player.image || player.photo || player.avatar || player.imageUrl || player.player?.image || player.player?.photo || player.player?.avatar || player.player?.imageUrl || player.athlete?.image || player.athlete?.photo || player.athlete?.avatar || player.athlete?.imageUrl || null
                if (!raw) return this.defaultPlayerImg
                if (/^https?:\/\//i.test(raw)) return raw
                return `${this.assetsUrl}/${raw}`
            },
            getTeamLogoSrc(player = {}) {
                const raw = player.teamLogo || player.team_logo || player.logo || player.team?.logo || null
                if (!raw) return null
                if (/^https?:\/\//i.test(raw)) return raw
                return `${this.assetsUrl}/${raw}`
            },
            getPlayerLegendLabel(player = {}, index = 0) {
                const jersey = player.jersey ?? player.number ?? player.shirtNumber ?? player.player?.jersey ?? player.player?.number ?? null
                const firstName = this.getPlayerFirstName(player)
                const lastName = this.getPlayerLastName(player)
                const fallbackName = player.playerName || player.name || player.athleteName || `Player ${index + 1}`
                const displayName = [lastName, firstName].filter(Boolean).join(' ').trim() || fallbackName

                return jersey !== null && jersey !== undefined && `${jersey}` !== ''
                    ? `#${jersey} ${displayName}`
                    : displayName
            },
            getPlayerKey(player = {}, index = 0) {
                return player.id || player._id || player.playerId || player.uuid || `${this.getPlayerLegendLabel(player, index)}-${index}`
            },
            getPlayerLastName(player = {}) {
                const directLastName = player.lastName || player.surname || player.lastname || player.familyName || player.athleteLastName || player.player?.lastName || player.player?.surname
                if (directLastName) {
                    return String(directLastName).trim()
                }

                const fullName = player.name || player.athleteName || player.playerName || player.player?.name || player.athlete?.name || ''
                const normalized = String(fullName || '').trim()
                if (!normalized) return ''

                if (normalized.includes(',')) {
                    return normalized.split(',')[0].trim()
                }

                const parts = normalized.split(/\s+/)
                return parts[parts.length - 1] || normalized
            },
            getPlayerFirstName(player = {}) {
                const directFirstName = player.firstName || player.givenName || player.athleteFirstName || player.player?.firstName || player.player?.givenName || player.athlete?.firstName
                if (directFirstName) {
                    return String(directFirstName).trim()
                }

                const fullName = player.name || player.athleteName || player.playerName || player.player?.name || player.athlete?.name || ''
                const normalized = String(fullName || '').trim()
                if (!normalized) return ''

                if (normalized.includes(',')) {
                    const rightSide = normalized.split(',')[1] || ''
                    return rightSide.trim().split(/\s+/)[0] || ''
                }

                const parts = normalized.split(/\s+/)
                return parts[0] || ''
            },
            getPirHistory(player = {}) {
                const stats = Array.isArray(player.pirHistory)
                    ? player.pirHistory
                    : (Array.isArray(player.stats) ? player.stats.filter(stat => stat && !stat.cup) : [])

                if (Array.isArray(player.pirHistory)) {
                    return player.pirHistory.map(value => Number(value) || 0)
                }

                return stats
                    .slice()
                    .sort((a, b) => {
                        const aMatch = Number(a.matchId)
                        const bMatch = Number(b.matchId)
                        if (Number.isFinite(aMatch) && Number.isFinite(bMatch)) {
                            return aMatch - bMatch
                        }
                        return String(a.matchId || '').localeCompare(String(b.matchId || ''))
                    })
                    .map(stat => Number(stat.pir) || 0)
            }
        }
    }
</script>

<style scoped>
.pir-progression-card {
    background: #fff;
}

.pir-progression-title {
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
}

.pir-progression-title-line {
    height: 1px;
    margin: 8px 0 10px;
    background: #e5e7eb;
}

.pir-ranking-wrap {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 2px 2px 8px;
}

.pir-ranking-row {
    display: grid;
    grid-template-columns: 34px 36px minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: linear-gradient(180deg, #fff 0%, #f8fafc 100%);
    position: relative;
    overflow: hidden;
}

.pir-ranking-row.is-clickable {
    cursor: pointer;
}

.pir-ranking-row.is-clickable:focus-visible {
    outline: 2px solid #f59e0b;
    outline-offset: 2px;
}

.pir-ranking-watermark {
    position: absolute;
    right: 10px;
    top: 50%;
    width: 70px;
    height: 70px;
    object-fit: contain;
    opacity: 0.12;
    transform: translateY(-50%) rotate(-10deg);
    pointer-events: none;
    z-index: 0;
}

.pir-ranking-rank,
.pir-ranking-avatar,
.pir-ranking-main,
.pir-ranking-value {
    position: relative;
    z-index: 1;
}

.pir-ranking-row.is-podium.rank-1 {
    border-color: #f59e0b;
    background: linear-gradient(180deg, #fffaf0 0%, #fff3d6 100%);
}

.pir-ranking-row.is-podium.rank-2 {
    border-color: #94a3b8;
    background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
}

.pir-ranking-row.is-podium.rank-3 {
    border-color: #b45309;
    background: linear-gradient(180deg, #fff7ed 0%, #ffedd5 100%);
}

.pir-ranking-rank {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    background: #111827;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pir-ranking-row.is-podium.rank-1 .pir-ranking-rank {
    background: #f59e0b;
}

.pir-ranking-row.is-podium.rank-2 .pir-ranking-rank {
    background: #94a3b8;
}

.pir-ranking-row.is-podium.rank-3 .pir-ranking-rank {
    background: #b45309;
}

.pir-ranking-avatar {
    width: 34px;
    height: 34px;
    object-fit: contain;
}

.pir-ranking-main {
    min-width: 0;
}

.pir-ranking-meta {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 4px;
}

.pir-ranking-name {
    color: #111827;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.pir-ranking-team {
    color: #6b7280;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}

.pir-ranking-track {
    width: 100%;
    height: 10px;
    border-radius: 999px;
    background: #e5e7eb;
    overflow: hidden;
}

.pir-ranking-bar {
    height: 100%;
    min-width: 0;
    border-radius: 999px;
    transition: width 0.3s ease;
    transform-origin: left center;
    animation: pirBarGrow 700ms ease both;
}

@keyframes pirBarGrow {
    0% {
        transform: scaleX(0);
        opacity: 0.4;
    }

    100% {
        transform: scaleX(1);
        opacity: 1;
    }
}

.pir-ranking-value {
    min-width: 44px;
    text-align: right;
    color: #111827;
    font-size: 14px;
    font-weight: 800;
}

.pir-empty-state {
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
    font-size: 14px;
    font-weight: 600;
}

@media (max-width: 600px) {
    .pir-ranking-row {
        grid-template-columns: 28px 32px minmax(0, 1fr) 40px;
        gap: 8px;
        padding: 8px;
    }

    .pir-ranking-meta {
        flex-direction: column;
        align-items: flex-start;
        gap: 1px;
    }

    .pir-ranking-name {
        font-size: 12px;
    }

    .pir-ranking-team {
        font-size: 10px;
    }

    .pir-ranking-value {
        font-size: 12px;
        min-width: 40px;
    }
}
</style>
